import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AgGridTable from '../../components/AgGridTable';
import SidebarPanel from '../../components/admin/SidebarPanel';
import ModelDocumentationModal from '../../components/shared/ModelDocumentationModal';
import ScopeEditorForm from './ScopeEditorForm';
import AlertMessage from '../../components/shared/alertMessage';
import { adminApi } from '../../services/api';
import { _setDisabled } from 'ag-grid-enterprise';

const ROLE_CATEGORIES = [
  { value: 'ankr_user', label: 'Ankr User' },
  { value: 'healthcare_user', label: 'Healthcare User' },
  { value: 'patient', label: 'Patient User' },
  { value: '', label: '---Sales Organization Roles---', _setDisabled },
  { value: 'org_user', label: 'Sales Organization User' },
  { value: 'other_org_user', label: 'Other Organization User' }
];

const RolesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roleId } = useParams();
  const gridRef = useRef(null);

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showPanel, setShowPanel] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ category: '', is_active: 'true', search: '', organization: '', health_system: '' });
  const [healthSystems, setHealthSystems] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [resources, setResources] = useState([]);
  const [showScopeEditor, setShowScopeEditor] = useState(false);
  const [alertStateSidePanel, setAlertStateSidePanel] = useState({ show: false, type: 'info', title: '', message: '' });
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category: '',
    description: '',
    order: 0,
    is_active: true,
    health_system_id: null,
    organization_id: null
  });

  useEffect(() => {
    fetchRoles();
    fetchHealthSystems();
    fetchOrganizations();
    fetchResources();
  }, [filters]);

  useEffect(() => {
    if (location.state?.mode === 'create') {
      setIsCreateMode(true);
      setSelectedRole(null);
      setShowPanel(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (roleId && roles.length > 0) {
      const role = roles.find(r => r.id === roleId);
      if (role) handleRowClick({ data: role });
    }
  }, [roleId, roles]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
      const res = await adminApi.get('/admin/roles/', { params });
      setRoles(res.data?.roles || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchHealthSystems = async () => {
    try {
      const res = await adminApi.getHealthSystems();
      setHealthSystems(res.data?.health_systems || []);
    } catch (err) {
      console.error('Failed to load health systems:', err);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const res = await adminApi.getOrganizations({ is_active: 'true' });
      setOrganizations(res.data?.organizations || []);
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  };

  const fetchResources = async () => {
    try {
      const response = await adminApi.get('/admin/scopes/resources/');
      if (response.data) {
        const resourcesArray = [];
        Object.entries(response.data).forEach(([categoryName, resourcesList]) => {
          if (Array.isArray(resourcesList)) {
            resourcesList.forEach(resourceInfo => {
              resourcesArray.push({
                id: resourceInfo.resource,
                resource: resourceInfo.resource,
                description: resourceInfo.description || '',
                category: resourceInfo.category || categoryName,
                visibility: resourceInfo.visibility !== false,
                cruds: resourceInfo.cruds || '',
                views: resourceInfo.views || []
              });
            });
          }
        });
        setResources(resourcesArray);
      }
    } catch (err) {
      console.error('Failed to load resources:', err);
    }
  };

  const handleRowClick = useCallback((event) => {
    setIsCreateMode(false);
    setSelectedRole(event.data);
    setShowPanel(true);
    navigate(`/admin/roles/${event.data.id}`, { replace: true });
  }, [navigate]);

  const handleClosePanel = () => {
    setShowPanel(false);
    setSelectedRole(null);
    setIsCreateMode(false);
    setFormData({
      name: '',
      slug: '',
      category: '',
      description: '',
      order: 0,
      is_active: true,
      health_system_id: null,
      organization_id: null
    });
    navigate('/admin/roles', { replace: true });
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post('/admin/roles/', formData);
      fetchRoles();
      handleClosePanel();
      alert('Role created successfully');
    } catch (err) {
      alert('Failed to create role: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdateRole = async () => {
    try {
      await adminApi.patch(`/admin/roles/${selectedRole.id}/`, {
        name: selectedRole.name,
        slug: selectedRole.slug,
        description: selectedRole.description,
        order: selectedRole.order,
        is_active: selectedRole.is_active,
        health_system_id: selectedRole.health_system_id,
        organization_id: selectedRole.organization_id
      });
      fetchRoles();
      setAlertStateSidePanel({ show: true, type: 'success', title: 'Success', message: 'Role updated successfully' });
    } catch (err) {
      setAlertStateSidePanel({ show: true, type: 'error', title: 'Failed to update role', message: err.response?.data?.message || err.message });
    }
  };

  const handleDeleteRole = async () => {
    if (!window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) return;

    try {
      await adminApi.delete(`/admin/roles/${selectedRole.id}/`);
      fetchRoles();
      handleClosePanel();
      alert('Role deleted successfully');
    } catch (err) {
      alert('Failed to delete role: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCellValueChanged = async (event) => {
    try {
      const { data, colDef, newValue } = event;
      await adminApi.patch(`/admin/roles/${data.id}/`, { [colDef.field]: newValue });
      fetchRoles();
    } catch (err) {
      alert('Failed to update: ' + (err.response?.data?.message || err.message));
      fetchRoles(); // Revert
    }
  };

  const handleExport = () => {
    gridRef.current?.api.exportDataAsCsv({
      fileName: `roles-${new Date().toISOString().split('T')[0]}.csv`
    });
  };

  const getCategoryLabel = (value) => {
    return ROLE_CATEGORIES.find(c => c.value === value)?.label || value;
  };

  const getScopeDisplay = (role) => {
    if (role.health_system) {
      return `HS: ${role.health_system.name}`;
    }
    if (role.organization) {
      return `Org: ${role.organization.name}`;
    }
    return 'Global';
  };

  const columnDefs = [
    { headerName: 'Name', field: 'name', width: 150, editable: true },
    { headerName: 'Slug', field: 'slug', width: 150 },
    {
      headerName: 'Category',
      valueGetter: p => getCategoryLabel(p.data?.category),
      width: 160
    },
    {
      headerName: 'Scope',
      valueGetter: p => getScopeDisplay(p.data),
      width: 140,
      cellRenderer: (params) => {
        const isGlobal = !params.data?.health_system && !params.data?.organization;
        return (
          <span className={`badge bg-${isGlobal ? 'secondary' : 'info'}`}>
            {params.value}
          </span>
        );
      }
    },
    { headerName: 'Description', field: 'description', width: 250, editable: true },
    {
      headerName: 'Order',
      field: 'order',
      width: 80,
      editable: true,
      cellEditor: 'agNumberCellEditor'
    },
    {
      headerName: 'Status',
      field: 'is_active',
      width: 90,
      cellRenderer: (params) => (
        <span className={`badge bg-${params.value ? 'success' : 'secondary'}`}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      headerName: 'Created',
      valueGetter: p => p.data?.created_at ? new Date(p.data.created_at).toLocaleDateString() : '-',
      width: 110
    }
  ];

  return (
    <>
      {error && (
        <div className="alert alert-danger">{error}</div>
      )}
      <div className="page-title-box d-flex align-items-center justify-content-between mb-3">
        <div className="col-8">
        <h4 className="text-lg">
          Roles & Permissions
        </h4>
        <p className="text-muted">Granular Management of Roles and Permissions for Healthcare and Sales Organization Users</p>
        </div>
        <div className="col-4 d-flex justify-content-end">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><a href="/admin">Admin</a></li>
              <li className="breadcrumb-item"><a href="">User Management</a></li>
              <li className="breadcrumb-item active">Roles</li>
            </ol>
          </nav>
        </div>
        </div>
      <div className="d-flex justify-content-end align-items-center gap-2 mb-3">
        <button className="btn btn-primary" onClick={() => navigate('/admin/roles', { state: { mode: 'create' } })}>
          <i className="fas fa-plus me-1" />Add Role
        </button>
      </div>
      <div className="card">
        <div className="d-flex justify-content-between mb-3 p-2">
          <div className='d-flex gap-2'>
            <select
              className="form-select form-select-sm"
              value={filters.category}
              onChange={e => setFilters({ ...filters, category: e.target.value })}
            >
              <option value="">All Categories</option>
              {ROLE_CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={filters.health_system}
              onChange={e => setFilters({ ...filters, health_system: e.target.value })}
            >
              <option value="">All Health Systems (+ Global)</option>
              <option value="__global__">Global Only</option>
              {healthSystems.map(hs => (
                <option key={hs.id} value={hs.id}>{hs.name}</option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={filters.organization}
              onChange={e => setFilters({ ...filters, organization: e.target.value })}
            >
              <option value="">All Organizations (+ Global)</option>
              <option value="__global__">Global Only</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={filters.is_active}
              onChange={e => setFilters({ ...filters, is_active: e.target.value })}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search roles..."
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
            />
            <vr></vr>
            <small className="text-muted text-nowrap align-self-center">
              Showing {roles.length} role{roles.length !== 1 ? 's' : ''}
            </small>
          </div>
            <div className='d-flex gap-2 justify-content-end'>
              <button className="btn btn-sm btn-info" onClick={() => setShowDocsModal(true)}>
                <i className="fas fa-book me-1" />Docs
              </button>
              <button className="btn btn-sm btn-success" onClick={handleExport}>
                <i className="fas fa-file-export me-1" />Export
              </button>
          </div>
        </div>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <AgGridTable
            ref={gridRef}
            rowData={roles}
            columnDefs={columnDefs}
            onRowClicked={handleRowClick}
            onCellValueChanged={handleCellValueChanged}
          />
        )}
      </div>

      <SidebarPanel
        isOpen={showPanel}
        onClose={handleClosePanel}
        title={isCreateMode ? "Create Role" : "Role Details"}
      >
        {alertStateSidePanel.show && (
          <AlertMessage
            type={alertStateSidePanel.type}
            title={alertStateSidePanel.title}
            message={alertStateSidePanel.message}
            dismissible={true}
            onDismiss={() => setAlertStateSidePanel({ ...alertStateSidePanel, show: false })}
            className="mb-3"
          />
        )}
        <div className="card">
          <div className="card-body p-3">
            {isCreateMode ? (
              <form onSubmit={handleCreateRole}>
                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Name *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      required
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                      placeholder="e.g., Engineer, Admin, Doctor"
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Slug *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      required
                      value={formData.slug}
                      onChange={e => setFormData({...formData, slug: e.target.value})}
                      placeholder="e.g., ankr_engineer, hs_admin"
                    />
                    <small className="text-muted">Unique identifier (lowercase, underscores)</small>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Category *</label>
                    <select
                      className="form-select form-select-sm"
                      required
                      value={formData.category}
                      onChange={e => setFormData({...formData, category: e.target.value, health_system_id: null, organization_id: null})}
                    >
                      <option value="">Select Category</option>
                      {ROLE_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {formData.category === 'healthcare_user' && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <label className="form-label small mb-1">Health System *</label>
                      <select
                        className="form-select form-select-sm"
                        required
                        value={formData.health_system_id || ''}
                        onChange={e => setFormData({...formData, health_system_id: e.target.value || null})}
                      >
                        <option value="">Select Health System</option>
                        {healthSystems.map(hs => (
                          <option key={hs.id} value={hs.id}>{hs.name}</option>
                        ))}
                      </select>
                      <small className="text-muted">Healthcare roles must be scoped to a health system</small>
                    </div>
                  </div>
                )}

                {(formData.category === 'org_user' || formData.category === 'other_org_user') && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <label className="form-label small mb-1">Organization *</label>
                      <select
                        className="form-select form-select-sm"
                        required
                        value={formData.organization_id || ''}
                        onChange={e => setFormData({...formData, organization_id: e.target.value || null})}
                      >
                        <option value="">Select Organization</option>
                        {organizations.map(org => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </select>
                      <small className="text-muted">Sales roles must be scoped to an organization</small>
                    </div>
                  </div>
                )}

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Description</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows="3"
                      value={formData.description}
                      onChange={e => setFormData({...formData, description: e.target.value})}
                      placeholder="Detailed description of this role..."
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1">Display Order</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={formData.order}
                      onChange={e => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                    />
                    <small className="text-muted">Lower numbers appear first</small>
                  </div>
                  <div className="col-6">
                    <label className="form-label small mb-1">Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={formData.is_active}
                      onChange={e => setFormData({...formData, is_active: e.target.value === 'true'})}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-12">
                    <button type="submit" className="btn btn-primary btn-sm me-2">
                      Create Role
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleClosePanel}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            ) : selectedRole && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="text-primary mb-0">{selectedRole.name}</h5>
                  <span className={`badge bg-${selectedRole.is_active ? 'success' : 'secondary'}`}>
                    {selectedRole.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="row g-2 small mb-3">
                  <div className="col-6">
                    <strong>Slug:</strong><br/>
                    <code>{selectedRole.slug}</code>
                  </div>
                  <div className="col-6">
                    <strong>Category:</strong><br/>
                    {getCategoryLabel(selectedRole.category)}
                  </div>
                  <div className="col-6">
                    <strong>Scope:</strong><br/>
                    <span className={`badge bg-${(!selectedRole.health_system && !selectedRole.organization) ? 'secondary' : 'info'}`}>
                      {getScopeDisplay(selectedRole)}
                    </span>
                  </div>
                  <div className="col-6">
                    <strong>Order:</strong><br/>
                    {selectedRole.order}
                  </div>
                  <div className="col-6">
                    <strong>Created:</strong><br/>
                    {new Date(selectedRole.created_at).toLocaleDateString()}
                  </div>
                  <div className="col-12">
                    <strong>Description:</strong><br/>
                    {selectedRole.description || '-'}
                  </div>
                </div>

                <hr />

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Name</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={selectedRole.name}
                      onChange={e => setSelectedRole({...selectedRole, name: e.target.value})}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Slug</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={selectedRole.slug}
                      onChange={e => setSelectedRole({...selectedRole, slug: e.target.value})}
                    />
                  </div>
                </div>

                {selectedRole.category === 'healthcare_user' && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <label className="form-label small mb-1">Health System</label>
                      <select
                        className="form-select form-select-sm"
                        value={selectedRole.health_system?.id || selectedRole.health_system_id || ''}
                        onChange={e => setSelectedRole({...selectedRole, health_system_id: e.target.value || null})}
                      >
                        <option value="">Select Health System</option>
                        {healthSystems.map(hs => (
                          <option key={hs.id} value={hs.id}>{hs.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {(selectedRole.category === 'org_user' || selectedRole.category === 'other_org_user') && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <label className="form-label small mb-1">Organization</label>
                      <select
                        className="form-select form-select-sm"
                        value={selectedRole.organization?.id || selectedRole.organization_id || ''}
                        onChange={e => setSelectedRole({...selectedRole, organization_id: e.target.value || null})}
                      >
                        <option value="">Select Organization</option>
                        {organizations.map(org => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label small mb-1">Description</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows="3"
                      value={selectedRole.description || ''}
                      onChange={e => setSelectedRole({...selectedRole, description: e.target.value})}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label small mb-1">Order</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={selectedRole.order}
                      onChange={e => setSelectedRole({...selectedRole, order: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small mb-1">Status</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedRole.is_active}
                      onChange={e => setSelectedRole({...selectedRole, is_active: e.target.value === 'true'})}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>

                <hr className="my-4" />

                {/* Scope Management Section */}
                <div className="mb-3">
                  <h6 className="text-primary mb-2">OAuth2 Scopes / Permissions</h6>
                  <p className="text-muted small mb-2">
                    Define resource-level permissions for this role using CRUDS notation (Create, Read, Update, Delete, Special).
                  </p>
                  {!showScopeEditor ? (
                    <>
                      {selectedRole.metadata?.scopes ? (
                        <div className="mb-3">
                          <small className="text-muted d-block mb-2">Current Scopes:</small>
                          <div className="d-flex flex-wrap gap-1 mb-2">
                            {selectedRole.metadata.scopes.split(' ').map((scope, idx) => (
                              <span key={idx} className="badge bg-info">
                                {scope}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted small mb-2">No scopes assigned to this role.</p>
                      )}
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setShowScopeEditor(true)}
                      >
                        <i className="fas fa-key me-1"></i>
                        Manage Scopes
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn-outline-secondary btn-sm mb-3"
                        onClick={() => setShowScopeEditor(false)}
                      >
                        <i className="fas fa-times me-1"></i>
                        Hide Scope Editor
                      </button>
                      <ScopeEditorForm
                        userData={{
                          id: selectedRole.id,
                          user_type: selectedRole.category,
                          role: selectedRole
                        }}
                        resources={resources}
                        onClose={() => setShowScopeEditor(false)}
                        onSaveSuccess={(message) => {
                          setShowScopeEditor(false);
                          fetchRoles();
                          setAlertStateSidePanel({ show: true, type: 'success', title: 'Success', message: message || 'Scopes updated successfully' });
                        }}
                      />
                    </>
                  )}
                </div>

                <hr className="my-4" />

                <div className="row mt-3">
                  <div className="col-12">
                    <button
                      className="btn btn-primary btn-sm me-2"
                      onClick={handleUpdateRole}
                    >
                      Save Changes
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={handleDeleteRole}
                    ><i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </SidebarPanel>

      <ModelDocumentationModal
        show={showDocsModal}
        onHide={() => setShowDocsModal(false)}
        modelType="role"
      />
    </>
  );
};

export default RolesPage;
